import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
// SC-SAAS-FRONTEND-2C — the main '@stripe/stripe-js' entry injects Stripe.js as an
// import side effect (this component ships inside dynamic-forms, so on every
// /programs/apply and /forms/form/submit page) and console.warns "Failed to load
// Stripe.js" whenever it's blocked. The /pure entry only loads on loadStripe().
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe } from '@stripe/stripe-js';
import { PaymentsService } from 'src/app/core/service/payments.service';
import { ApiEndpointService } from 'src/app/core/service/api-endpoint.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Location } from '@angular/common';
import { BillingFormComponent } from '../billing-form/billing-form.component';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { IGlobalSettings } from 'src/app/core/domain/settings.model';
import { Observable, Subject } from 'rxjs';
import { getBrandDetails, getGlobalSettings } from 'src/app/core/state/global';
import { select, Store } from '@ngrx/store';
import { DateHelperService } from 'src/app/core/service/date-helper.service';
import { FacilitiesService } from 'src/app/core/service/facilities.service';
import { ToastAlertService } from 'src/app/shared/utils/toast.service';

declare var paypal: any;
declare var Razorpay: any;
declare var EasebuzzCheckout: any;

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
})
export class CheckoutComponent implements OnInit, AfterViewInit {
  @ViewChild(BillingFormComponent) billingFormComponent!: BillingFormComponent;
  currency: string;
  @Input() passData: any;

  paymentForm: FormGroup;
  stripe: Stripe | null = null;
  selectedMethod: string | null = null;
  paymentMethods: any[] = [];
  defaultMethod: string;

  gstAmount = 0;
  totalAmount = 0;
  subTotal=0;
  discountAmount = 0;
  discountedPrice = 0;
  discounted_originalPrice = 0;

  coupons: any[] = [];
  appliedCoupon: any = null;
  showCouponInput = true;
  billing_formData: any;
  billing_Form_Valid: boolean = false;
  invalidRequiredFields: string[] = [];
  displayCharges: { name: string; value:string;price: number,type:string }[] = [];

  public globalSettings$: Observable<IGlobalSettings>;
  globalSettings: IGlobalSettings;
  private destroy$ = new Subject<void>();
  isInvoice:boolean=false
  is_intra_state:boolean =false;

  startDate: string | null = this.dateHelper.formatDateLocal(new Date());
  endDate: string | null = null; 
  constructor(
    private fb: FormBuilder,
    private activeModal: NgbActiveModal,
    private paymentService: PaymentsService,
    private ngxLoader: NgxUiLoaderService,
    private location: Location,
    private store: Store,
    private dateHelper: DateHelperService,
    private facilitiesService:FacilitiesService,
    private toastAlertService: ToastAlertService
  ) {}

  ngOnInit(): void {
    const state = this.location.getState() as any;
    console.log(this.passData, 'pass');

      this.store.pipe(select(getBrandDetails)).pipe(distinctUntilChanged()).subscribe((res) => {
          if (res) {
             this.currency= res.features.payment_currency ;
          }
        })
    this.defaultMethod =
      this.paymentMethods.find((m) => m.isPrimary)?.code || '';
    this.paymentForm = this.fb.group({ paymentMethod: [this.defaultMethod] });
    this.paymentForm.get('paymentMethod')?.valueChanges.subscribe((method) => {
      this.selectedMethod = method;
      if (method === 'paypal') this.startCheckoutPaypal();
    });

    this.selectedMethod = this.defaultMethod;

    this.getPaymentMethods();
    this.recomputeEndDate();
    this.calculateGST();
  }

  ngAfterViewInit(): void {
    if (this.selectedMethod === 'paypal') {
      this.startCheckoutPaypal();
    }
  }

  getPaymentMethods(): void { 
    this.paymentService.gateWayClientIdData().subscribe(
      (response: any) => {
        this.paymentMethods = response;
        this.defaultMethod =
          this.paymentMethods.find((m) => m.isPrimary)?.code || '';
        this.selectedMethod = this.defaultMethod;
        console.log('selectedMethod set to:', this.selectedMethod);

        this.paymentForm.patchValue({ paymentMethod: this.defaultMethod });

        this.paymentForm
          .get('paymentMethod')
          ?.valueChanges.subscribe((method) => {
            this.selectedMethod = method;
            console.log('selectedMethod changed to:', this.selectedMethod);
            if (method === 'paypal') this.startCheckoutPaypal();
          });
      },
      (error) => {
        console.error('Failed to fetch payment methods', error);
      }
    );
  }

  // Paypal gateWay
  async startCheckoutPaypal() {
    const clientId = this.paymentMethods.find(
      (m) => m.code === 'paypal'
    )?.clientId;
    if (!clientId) return console.error('PayPal Client ID not found.');

    await this.loadPaypalScript(clientId);

    if (!paypal || typeof paypal.Buttons !== 'function') return;

    paypal
      .Buttons({
        createOrder: async () => {
          const payload = this.buildPaymentPayload('paypal');
          const order = await this.paymentService
            .createOrderBy(payload)
            .toPromise();
          return order?.data?.gatewayData?.id;
        },
        onApprove: async (data) => {
          await this.paymentService.capturePaypalOrder(data.orderID);
          this.redirectToSuccess(data.orderID, 'paypal', data.orderNumber);
        },
        onCancel: () => this.redirectToFailed(),
        onError: (err) => localStorage.removeItem('order'),
      })
      .render('#paypal-button-container');
  }

  private loadPaypalScript(clientId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        'script[src*="paypal.com/sdk/js"]'
      );
      if (existingScript) existingScript.remove();

      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = (err) => reject(err);
      document.body.appendChild(script);
    });
  }

  // Razorpay gateWay
  async startRazorpayCheckout() {
    this.ngxLoader.start('checkout-loader');
    const key = this.paymentMethods.find(
      (m) => m.code === 'razorpay'
    )?.clientId;
    if (!key) return;

    await this.loadRazorpayScript();
    const payload = this.buildPaymentPayload('razorpay');
    const response = await this.paymentService.createOrderBy(payload).toPromise();
    const order = response.data.gatewayData;

    const options = {
      key,
      amount: order.amount,
      currency: order.currency,
      name: response.data.dbOrder.productName,
      description: 'Order Payment',
      order_id: order.id,
      handler: (res: any) => {
        console.log(res, 'res');

        this.redirectToSuccess(order.id, 'razorpay', response.data.dbOrder.orderNumber);
      },
      modal: {
        ondismiss: () => this.redirectToFailed(),
      },
      prefill: {
        name: payload.fullName,
        email: payload.email,
        contact: payload.phoneNumber,
      },
      theme: { color: '#3399cc' },
    };

    new Razorpay(options).open();
    this.ngxLoader.stop('checkout-loader');
  }

  private loadRazorpayScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.querySelector('script[src*="razorpay.com"]'))
        return resolve();

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(this.ngxLoader.stop('checkout-loader'));
      script.onerror = () => reject(this.ngxLoader.stop('checkout-loader'));
      document.body.appendChild(script);
    });
  }

  // Stripe gateWay
  async redirectToStripeCheckout() {
    this.ngxLoader.start('checkout-loader');
    const stripeKey = this.paymentMethods.find(
      (m) => m.code === 'stripe'
    )?.clientId;
    let stripe: Stripe | null = null;
    try {
      stripe = await loadStripe(stripeKey || '');
    } catch (e) {
      this.ngxLoader.stop('checkout-loader');
      this.toastAlertService.showToast('Stripe is unavailable right now, please try another payment method', 'error');
      return;
    }
    const encodedGateway = btoa('stripe');
    const payload = this.buildPaymentPayload('stripe');

    payload.successUrl =
      this.passData.url +
      `payment/success?orderkey={CHECKOUT_SESSION_ID}&gateway=${encodedGateway}`;
    payload.cancelUrl = this.passData.url + 'payment/failed';

    const response = await this.paymentService.createOrderBy(payload).toPromise();
    const sessionId = response?.data?.gatewayData?.sessionId;

    this.redirectToSuccess(sessionId, 'stripe', response?.data?.dbOrder?.orderNumber);

    if (stripe && sessionId) {
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) console.error('Stripe Checkout Error:', error.message);
    }

    this.ngxLoader.stop('checkout-loader');
  }

  async startEasebuzzCheckout() {
    this.ngxLoader.start('checkout-loader');

    const easebuzzMethod = this.paymentMethods.find(
      (m) => m.code === 'easebuzz'
    );
    const clientId = easebuzzMethod?.clientId;

    if (!clientId) {
      this.ngxLoader.stop('checkout-loader');
      return;
    }

    const payload = this.buildPaymentPayload('easebuzz');

    try {
      const response: any = await this.paymentService
        .createOrderBy(payload)
        .toPromise();
      const order = response?.data?.gatewayData;
      const accessKey = order?.access_key;
      const paymentMode = order?.paymentMode;

      if (!accessKey) {
        this.ngxLoader.stop('checkout-loader');
        return;
      }

      // SDK Check
      const EasebuzzCheckout = (window as any).EasebuzzCheckout;
      if (!EasebuzzCheckout) {
        this.ngxLoader.stop('checkout-loader');
        return;
      }
    //  return;
      // Enable iframe mode: pass `true` as 3rd arg
      const easebuzzCheckout = new EasebuzzCheckout(clientId, paymentMode, true);
      easebuzzCheckout.initiatePayment({
        access_key: accessKey,
        onResponse: (res: any) => {
          if (res.status === 'success' || res.status === 'pending') {
            this.redirectToSuccess(order.txnid, 'easebuzz', order.orderNumber);
          } else {
            this.redirectToFailed();
          }
        },
        theme: '#123456',
      });
    } catch (error) {
      this.redirectToFailed();
      localStorage.removeItem('order');
    } finally {
      this.ngxLoader.stop('checkout-loader');
    }
  }
  // PayU Gateway
  async startPayUCheckout() {
    this.ngxLoader.start('checkout-loader');

    const payload = this.buildPaymentPayload('payu');

    try {
      const response: any = await this.paymentService
        .createOrderBy(payload)
        .toPromise();
      const order = response?.data?.gatewayData;

      if (!order?.payuUrl || !order?.txnid) {
        this.ngxLoader.stop('checkout-loader');
        return;
      }

      // Store order info for success redirect
      const encodedOrderkey = btoa(order.txnid);
      const encodedGateway = btoa('payu');
      localStorage.setItem('order', JSON.stringify({
        orderKey: encodedOrderkey,
        gateway: encodedGateway,
      }));

      // Build and submit a hidden form POST to PayU
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = order.payuUrl;

      const fields = {
        key: order.key,
        txnid: order.txnid,
        amount: order.amount,
        productinfo: order.productinfo,
        firstname: order.firstname,
        email: order.email,
        phone: order.phone,
        surl: order.surl,
        furl: order.furl,
        hash: order.hash,
      };

      for (const [name, value] of Object.entries(fields)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value || '';
        form.appendChild(input);
      }

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      this.ngxLoader.stop('checkout-loader');
      this.redirectToFailed();
      localStorage.removeItem('order');
    }
  }

  private appendQueryParams(url: string, params: Record<string, string>): string {
    if (!url) return url;

    try {
      const parsed = new URL(url, window.location.origin);
      for (const [key, value] of Object.entries(params)) {
        parsed.searchParams.set(key, value);
      }
      return parsed.toString();
    } catch {
      const hasQuery = url.includes('?');
      const query = Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
      return `${url}${hasQuery ? '&' : '?'}${query}`;
    }
  }

   createDiscountedTransaction(){
     this.ngxLoader.start('checkout-loader');
    const payload = this.buildPaymentPayload('discounted')
    this.paymentService.createDiscountedTransaction(payload).subscribe({
    next: (res: any) => {

      this.ngxLoader.stop('checkout-loader');
      const orderId =res.data.dbOrder.paymentReference
      const orderNumber = res.data.dbOrder.orderNumber
      this.redirectToSuccess(orderId, 'discounted', orderNumber);
    },
    error: (err: any) => {
      this.ngxLoader.stop('checkout-loader');
      this.redirectToFailed();
    
    },
  });
  }

  private buildPaymentPayload(gateway: string): any {
    const hasTax = !!this.gstAmount;
    const hasCharges = Array.isArray(this.passData?.charges) && this.passData.charges.length > 0;

    const payload: any = {
      gateway,
      amount: Number(this.passData?.selectedPrice) ?? 0,
      finalAmount: this.totalAmount,
      taxAmount: this.gstAmount ?? 0,
      taxId:this.passData.taxProfile?.id ?? null,
      customerGSTIN:this.billing_formData?.customerGST ?? '',
      discountAmount: Number(this.discountAmount) ?? 0,
      currency: gateway == 'paypal' ? 'USD' : this.currency,
      productName: this.passData.name ?? this.passData?.data?.name,
      moduleType: this.passData.moduleType,
      moduleTypeId: this.passData.moduleTypeId ?? this.passData.id,
      userId: this.passData.applicantId ?? null, //application submission id
      couponId: this.appliedCoupon?.id ?? null,
      chargesTypes:this.passData.chargesTypes,
      name:this.billing_formData?.fullName ?? this.passData?.activeUser?.name,
      phoneNumber:this.billing_formData?.phone ?? this.passData?.activeUser?.mobileNumber,
      email: this.billing_formData?.email ?? this.passData?.activeUser?.email,
      address: this.billing_formData?.address ?? '',
      country: this.billing_formData?.countryName ?? '',
      state: this.billing_formData?.stateName ?? '',
      city: this.billing_formData?.cityName ?? '',
      pincode:this.billing_formData?.pincode != null? String(this.billing_formData.pincode): '',
      successUrl: this.passData.url + 'payment/success',
      cancelUrl: this.passData.url + 'payment/failed',
      planType: this.passData.selectedCycle?.toLowerCase() ?? null,
      redirectUrl: this.passData.url + this.passData.activeProgramUrl,
      moduleTypeSubId: this.passData.moduleTypeSubId ?? null,
      bookingId:this.passData?.bookingId||null,

      tax:hasTax ? {
      name: this.passData?.taxProfile?.name,
      tax_percent: this.passData?.taxProfile?.taxPercent ,
      amount: this.gstAmount,
      is_gst: this.passData?.taxProfile?.isGST,
      is_intra_state:this.is_intra_state,
      other: this.passData?.taxProfile?.isGST && this.isInvoice ? [
    {
      name: 'CGST',
      amount: this.gstAmount / 2,
      tax_percent: (this.passData?.taxProfile?.taxPercent / 2).toString()
    },
    {
      name: 'SGST',
      amount: this.gstAmount / 2,
      tax_percent: (this.passData?.taxProfile?.taxPercent / 2).toString()
    }
  ] : []

    }:null,
    discount:this.discountAmount !=0 ? {
      name: this.appliedCoupon?.name ,
      code: this.appliedCoupon?.code ,
      type: this.appliedCoupon?.type ,
      value: this.appliedCoupon?.value,
      amount: this.discountAmount,
    }:null ,
    charges: hasCharges ? this.displayCharges.map((charge: any) => ({
      name: charge.name,
      type: charge.type,
      value: charge.value,
      amount: charge.price,
    })):null,
      membershipData:  this.passData.paymentType == 'membership'
        ? {
            membership_type_id: this.passData.membershipid,
            membership_type_name: this.passData?.name ?? null,
            profile_type: this.passData.activeUser.accountType ?? null,
            profile_id: this.passData.applicantId?? null,
            profile_name: this.passData.profileName ?? null,
            booking_duration:this.membershipMonths,
            pricing_duration:this.passData?.selectedCycle,
            start_date:  this.startDate,
            end_date:this.endDate
          }:null,
     paymentType:this.passData.paymentType
    };

    return payload;
  }

  private redirectToSuccess(orderId: string, gateway: string, orderNumber: string): void {
    if(gateway=='discounted'){
         window.location.href =  this.passData.url + this.passData.activeProgramUrl;
    }else{
    const encodedOrderkey = btoa(orderId);
    const encodedGateway = btoa(gateway);
    const bookingId = btoa(this.passData?.bookingId || null);

    const orderData = {
      orderKey: encodedOrderkey,
      gateway: encodedGateway,
      bookingId: bookingId,
      orderNumber: orderNumber
    };

    localStorage.setItem('order', JSON.stringify(orderData));
    const validGateways = ['paypal', 'razorpay', 'easebuzz', 'payu'];

    if (validGateways.includes(gateway)) {
      const { url ,moduleType } = this.passData;
      if(moduleType== 'lms_courses'){
         this.activeModal.close({ status: 'success',paymentProvider: gateway,url:url,encodedOrderkey:encodedOrderkey,encodedGateway:encodedGateway,orderNumber:orderNumber});
      }else{
      window.location.href = `${url}payment/success?orderkey=${encodedOrderkey}&gateway=${encodedGateway}`;
      }
    }
  }
  }
  private redirectToFailed(): void {
    localStorage.removeItem('order');
    const lastUrl = this.passData.url + this.passData.activeProgramUrl;

    const encodedLastUrl = encodeURIComponent(lastUrl);
    window.location.href = `${this.passData.url}payment/failed?lastUrl=${encodedLastUrl}`;
  }

 recomputeEndDate(): void {
   console.log("this.recomputeEndDate");
 
  const selectedStartDate = this.startDate || this.dateHelper.formatDateLocal(new Date());
  const {
    selectedCycle: duration,
    membershipid,
    expiryMonth,
    isHardExpiry,
    maxYears,
    currentMembershipEndDate,
    isMembershipRenewOrUpgrade,
  } = this.passData || {};
 
  if (!selectedStartDate || !membershipid) return;
 
  const startDateObj = this.dateHelper.parseDateLocal(selectedStartDate);
  if (!startDateObj) return;

  let effectiveStartDate = startDateObj;
  const currentMembershipEndDateObj = this.dateHelper.parseDateLocal(currentMembershipEndDate);
  if (
    Boolean(isMembershipRenewOrUpgrade) &&
    currentMembershipEndDateObj &&
    currentMembershipEndDateObj.getTime() >= startDateObj.getTime()
  ) {
    effectiveStartDate = this.dateHelper.addDays(currentMembershipEndDateObj, 1);
  }

  // For new yearly purchases with hard expiry, the membership starts on the
  // first day of the month following the expiry month, anchored to the cycle
  // currently in progress: the most recent such cycle-start that is on or
  // before today.
  //   expiryMonth = 3 (March), today in Jun 2026 -> 01-04-2026 (end 31-03-2027)
  //   expiryMonth = 6 (June),  today in Jun 2026 -> 01-07-2025 (end 30-06-2026)
  {
    const hardExpiryEnabled =
      typeof isHardExpiry === 'boolean' ? isHardExpiry : isHardExpiry !== null && isHardExpiry !== undefined;
    const expiryMonthNum = Number(expiryMonth);
    if (
      duration === 'yearly' &&
      !Boolean(isMembershipRenewOrUpgrade) &&
      hardExpiryEnabled &&
      Number.isFinite(expiryMonthNum) &&
      expiryMonthNum >= 1 &&
      expiryMonthNum <= 12
    ) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      // expiryMonthNum is 1-indexed; used as a 0-indexed month it is the month
      // AFTER the expiry month (Dec -> Jan of next year rolls over automatically).
      let cycleStart = new Date(today.getFullYear(), expiryMonthNum, 1);
      if (cycleStart.getTime() > today.getTime()) {
        // This year's cycle hasn't started yet; we're still inside last year's.
        cycleStart = new Date(today.getFullYear() - 1, expiryMonthNum, 1);
      }
      effectiveStartDate = cycleStart;
    }
  }

  let endDateObj: Date | null = null;
 
  const cycleMonths: Record<string, number> = {
    monthly: 1,
    quarterly: 3,
    halfYearly: 6,
    yearly: 12,
  };
 
  if (["yearly", "lifetime"].includes(duration)) {
    const years =
      duration === "lifetime"
        ? parseInt(maxYears || "0", 10)
        : this.membershipMonths;

    if (years > 0) {
      const hardExpiryEnabled =
        typeof isHardExpiry === 'boolean' ? isHardExpiry : isHardExpiry !== null && isHardExpiry !== undefined;
      const expiryMonthNum = Number(expiryMonth);

      if (
        !Boolean(isMembershipRenewOrUpgrade) &&
        hardExpiryEnabled &&
        Number.isFinite(expiryMonthNum) &&
        expiryMonthNum >= 1 &&
        expiryMonthNum <= 12
      ) {
        const startMonth = effectiveStartDate.getMonth() + 1;
        const baseYear =
          startMonth > expiryMonthNum
            ? effectiveStartDate.getFullYear() + 1
            : effectiveStartDate.getFullYear();
        endDateObj = this.dateHelper.getLastDateOfExpiryMonth(
          baseYear + years - 1,
          expiryMonthNum
        );
      } else {
        endDateObj = this.dateHelper.addYearsMinusOneDay(effectiveStartDate, years);
      }
    }
  } 
   console.log(effectiveStartDate,"startDateObj, totalMonths")
  if (!endDateObj && duration && cycleMonths[duration]) {
    const totalMonths = cycleMonths[duration] * this.membershipMonths;
    console.log(effectiveStartDate, totalMonths,"startDateObj, totalMonths")
    endDateObj = this.dateHelper.addMonthsMinusOneDay(effectiveStartDate, totalMonths);
  }

  // For new purchases (not renew/upgrade), hard-expiry means the membership cannot extend past the expiry month boundary.
  if (!Boolean(isMembershipRenewOrUpgrade) && endDateObj) {
    const hardExpiryEnabled =
      typeof isHardExpiry === 'boolean' ? isHardExpiry : isHardExpiry !== null && isHardExpiry !== undefined;
    const expiryMonthNum = Number(expiryMonth);

    if (
      hardExpiryEnabled &&
      Number.isFinite(expiryMonthNum) &&
      expiryMonthNum >= 1 &&
      expiryMonthNum <= 12 &&
      !['yearly', 'lifetime'].includes(duration)
    ) {
      const startMonth = effectiveStartDate.getMonth() + 1;
      const baseYear =
        startMonth > expiryMonthNum
          ? effectiveStartDate.getFullYear() + 1
          : effectiveStartDate.getFullYear();
      const hardExpiryBoundary = this.dateHelper.getLastDateOfExpiryMonth(baseYear, expiryMonthNum);
      if (endDateObj.getTime() > hardExpiryBoundary.getTime()) {
        endDateObj = hardExpiryBoundary;
      }
    }
  }
 
  this.startDate = this.dateHelper.formatDateLocal(effectiveStartDate);
  this.endDate = endDateObj ? this.dateHelper.formatDateLocal(endDateObj) : null;
 
  console.log(this.startDate, this.endDate, "endDate");
 }

  closeDialog(): void {
    this.activeModal.close({status: 'closed'});
  }

membershipMonths = 1; 

incrementMonths(): void {
  if(  this.membershipMonths< this.passData?.maxDurationForMultipleBooking)
  this.membershipMonths++;
  this.recomputeEndDate()
  this.calculateGST(); 
}

decrementMonths(): void {
  if (this.membershipMonths > 1) {
    this.membershipMonths--;
    this.recomputeEndDate()
    this.calculateGST(); 
  }
}

calculateGST(): void {
  const gstRate = this.passData?.taxProfile ? Number(this.passData.taxProfile.taxPercent) : 0;

  // multiply base price by membership months
  const basePrice = (Number(this.passData?.selectedPrice) || 0) * this.membershipMonths;
  let totalCharges = 0;
  this.displayCharges = [];

  // Step 1: Add additional charges (amount or percentage)
  if (this.passData?.isChargesIncluded && this.passData.charges?.length) {
    for (const charge of this.passData.charges) {
      let value = 0;
      if (charge.type === 'amount') {
        value = parseFloat(charge.value);
      } else if (charge.type === 'percentage') {
        value = (basePrice * parseFloat(charge.value)) / 100;
      }
      totalCharges += value;

      this.displayCharges.push({
        name: charge.name,
        value: charge.value,
        price:+value.toFixed(2),
        type:charge.type
      });
    }
  }

  this.subTotal = basePrice + totalCharges;
// Step 2:applied Coupon (amount or percentage)
  if (this.appliedCoupon) {
    const coupon = this.appliedCoupon;
    if (coupon.type === 'amount') {
      this.discountAmount = parseFloat(coupon.value);
    } else if (coupon.type === 'percentage') {
      this.discountAmount = (this.subTotal * parseFloat(coupon.value)) / 100;
    }
    this.subTotal -= this.discountAmount;
  } else {
    this.discountAmount = 0;
  }

  //subTotal is not negative
  this.subTotal = Math.max(this.subTotal, 0);
  
// Step 3: If Tax applies
  this.gstAmount = Math.max(+((this.subTotal * gstRate) / 100).toFixed(2), 0);
  this.totalAmount = Math.max(+(this.subTotal + this.gstAmount).toFixed(2), 0);

  
  this.paymentService.getCouponList().subscribe({
    next: (res: any) => {
      this.coupons = res;
    },
  });
}


removeCoupon(): void {
  this.appliedCoupon = null;
  this.discountAmount = 0;
  this.calculateGST();
  this.showCouponInput = true;
}

onCouponApplied(coupon: any): void {
  this.applyCoupon(coupon);
  this.showCouponInput = true;
}

applyCoupon(coupon: any): void {
  this.appliedCoupon = coupon;
  this.calculateGST();
}

onBillingFormChange(formData?: any) {
    this.globalSettings$ = this.store.pipe(select(getGlobalSettings));
    this.globalSettings$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((res) => {
      if (res) {
        this.globalSettings = res
      }
    })
    const { supplierCountry, supplierState } = this.globalSettings.invoice;

    this.billing_Form_Valid = formData.isValid;
    this.billing_formData = formData.payload;
    this.invalidRequiredFields = formData.invalidRequiredFields || [];

    if (this.billing_formData && this.billing_formData.countryName === supplierCountry && this.billing_formData.stateName === supplierState) {
      this.isInvoice = true;
      this.is_intra_state =true;
    } else {
      this.isInvoice = false;
      this.is_intra_state =false;
    }
  }

  highlightFields(): void {
    if (this.billingFormComponent && this.invalidRequiredFields?.length) {
      this.billingFormComponent.highlightInvalidFields(
        this.invalidRequiredFields
      );
    }
  }

  get showPayNow(): boolean {
    return ['stripe', 'razorpay', 'easebuzz', 'payu'].includes(this.selectedMethod);
  }

  handlePayment() {
    switch (this.selectedMethod) {
      case 'stripe':
        this.redirectToStripeCheckout();
        break;
      case 'razorpay':
        this.startRazorpayCheckout();
        break;
      case 'easebuzz':
        this.startEasebuzzCheckout();
        break;
      case 'payu':
        this.startPayUCheckout();
        break;
    }
  }

   ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
 
  }
}
